import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useNow } from "@/hooks/useNow";
import type { TreatBrief } from "@/lib/types";
import { SkeletonCard } from "@/components/base/Skeleton";
import TreatCard from "@/pages/treat/components/TreatCard";

// 메인 홈 «오늘은 내가 쏜다» 섹션.
// treats.php?today=1 은 «오늘 등록된» 번개만 내려준다.
// 오늘 글이 0건이어도 섹션은 항상 보이며, 안내 카드로 첫 등록을 유도한다.
export default function TreatSection() {
  const { data, loading, serverTs, reload } = useApi<TreatBrief[]>("treats.php?today=1");
  const now = useNow();

  const list = data ?? [];
  const elapsed = serverTs ? Math.max(0, now / 1000 - serverTs) : 0;
  const scroll = list.length >= 3;

  return (
    <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              <span aria-hidden="true">🍚</span>
              오늘은 내가 쏜다
            </h2>
            <p className="mt-2 text-foreground-600">
              예: 오늘 점심 쏠 테니 선착순으로 오세요.
            </p>
          </div>
          <Link
            to="/treat"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
          >
            전체 보기
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-hand-heart-line" aria-hidden="true"></i>
            </span>
            <p className="font-heading text-base font-semibold text-foreground-800">
              오늘은 아직 쏘는 사람이 없습니다.
            </p>
            <p className="mt-1.5 text-sm text-foreground-500">
              첫 번째가 되어보세요!
            </p>
            <Link
              to="/treat"
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-add-line text-base" aria-hidden="true"></i>
              내가 쏜다
            </Link>
          </div>
        ) : scroll ? (
          <div className="-mx-4 flex gap-5 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
            {list.map((t) => (
              <div key={t.no} className="w-[320px] shrink-0">
                <TreatCard brief={t} elapsed={elapsed} onReload={reload} />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-5 ${
              list.length > 1 ? "md:grid-cols-2" : ""
            }`}
          >
            {list.map((t) => (
              <TreatCard key={t.no} brief={t} elapsed={elapsed} onReload={reload} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}